import React, { useEffect, useState } from "react";
import { Button, Drawer, message, Switch, Table, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { SettingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import styles from "./tenants.module.css";
import { Loading } from "../../components/loading";
import { ApplicationForm } from "./applicationForm";
import { formatDate } from "../../utils/formatDate";
import $authHost from "../../http/auth";
import { getUser } from "../selectors";

interface Application {
  tenantId: string;
  applicationId: number;
  applicationName: string;
  currentVersionId: number;
  number: string;
  configuration: null;
  baseConfiguration: null;
  schema: null;
  isEnabled: boolean;
  enabledTill: null;
  id: number;
  createdOn: string;
  updatedOn: string | null;
  deletedOn: string | null;
}

export const TenantApps = ({ tenant }: any) => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState<boolean>(true);
  const [selectedApplication, setSelectedApplication] = useState<string>("");
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const user = useSelector(getUser);
  const { t } = useTranslation("tenants");

  useEffect(() => {
    if (!tenant) {
      return;
    }

    Promise.all([
      $authHost.get(`/tenants/${user.role}/${tenant.id}/applications?isCreated=true`),
      $authHost.get(`/tenants/${user.role}/${tenant.id}/applications?isCreated=false`),
    ])
      .then((res) => {
        let results: any = [];
        res.forEach((item) => {
          results = results.concat(item.data);
        });
        setApplications(results);
        setApplicationsLoading(false);
      })
      .catch((err) => {
        console.log(err?.message);
      });
  }, [tenant]);

  const configColumns: ColumnsType<Application> = [
    {
      title: t("name"),
      dataIndex: "applicationName",
      key: "applicationName",
      render: (value, row) => (
        <span
          className={styles.tableCellLink}
          onClick={() => {
            setSelectedApplication(value);
            setSelectedApplicationId(row.applicationId);
            setOpen(true);
          }}
        >
          {value}
        </span>
      ),
    },
    {
      title: t("enabled"),
      dataIndex: "isEnabled",
      key: "isEnabled",
      render: (value, row) => (
        <Tooltip title={t(value ? "disable" : "enable")}>
          <Switch
            checked={value}
            onClick={(val: boolean) => {
              toggleTenantApp(val, row);
            }}
          />
        </Tooltip>
      ),
    },
    {
      align: "center",
      title: t("validTill"),
      dataIndex: "enabledTill",
      key: "enabledTill",
      render: (value: string) => (value ? formatDate(value) : <hr className={styles.emptyValue} />),
    },
    {
      align: "center",
      title: t("settings"),
      dataIndex: "applicationName",
      key: "settings",
      render: (value, row) => {
        const enabled = row.isEnabled;

        return enabled ? (
          <Button className={styles.applicationListSettingsButton}>
            <SettingOutlined
              style={{ fontSize: "24px", cursor: "pointer" }}
              className={styles.tableCellIconLink}
              onClick={() => {
                setSelectedApplication(value);
                setSelectedApplicationId(row.applicationId);
                setOpen(true);
              }}
            />
          </Button>
        ) : (
          <Tooltip title={t("enableTheApplication")}>
            <div>
              <Button className={styles.applicationListSettingsButton} disabled>
                <SettingOutlined style={{ fontSize: "24px" }} className={styles.tableCellIconLink} />
              </Button>
            </div>
          </Tooltip>
        );
      },
    },
  ];

  const onClose = () => {
    setOpen(false);
    setSelectedApplication("");
    setSelectedApplicationId(null);
  };

  const toggleTenantApp = (isActive: boolean, row: any) => {
    $authHost.post(`/tenants/${user.role}/${tenant.id}/application/${row.applicationId}/toggle`).then((res) => {
      setApplications((applications) =>
        applications.map((app) => (app.applicationId === res.data.applicationId ? res.data : app))
      );
      message.success(t(`applicationSuccessfully${res.data.isEnabled ? "Enabled" : "Disabled"}`), 5);
    });
  };

  const onApplicationSave = (updatedApplication: Application) => {
    setApplications((applications) =>
      applications.map((application) =>
        application.applicationId === updatedApplication.applicationId ? updatedApplication : application
      )
    );
    onClose();
  };

  return (
    tenant && (
      <>
        <h2 className={styles.h2}>
          {t("tenant")}: {`${tenant.name} ${tenant.email}`}
        </h2>
        {applicationsLoading ? (
          <Loading />
        ) : (
          <Table
            className={styles.appsTable}
            columns={configColumns}
            dataSource={applications}
            pagination={false}
            rowKey="applicationId"
          />
        )}
        <Drawer
          title={t("settings")}
          placement="right"
          onClose={onClose}
          open={open}
          mask={true}
          width={window.innerWidth > 768 ? 700 : window.innerWidth}
          destroyOnClose={true}
        >
          <ApplicationForm
            appName={selectedApplication}
            tenantId={tenant.id}
            applicationId={selectedApplicationId}
            onApplicationSave={onApplicationSave}
          />
        </Drawer>
      </>
    )
  );
};
